// src/pages/ContractInsightPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import ContractSchemaSelector from "../components/ContractSchemaSelector";
import { isIOSApp } from "../utils/platform";
import {
  abandonContractInsightJob,
  CONTRACT_INSIGHT_MAX_FILE_BYTES,
  CONTRACT_INSIGHT_MAX_FILE_MB,
  createContractInsightJob,
  startContractInsightRun,
  uploadContractInsightFile,
} from "../utils/contractApi";

const HOST = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const CONTRACT_INSIGHT_PAGE_TIERS = [25, 50, 75, 150, 250];
const MONTHLY_PLAN_TYPES = new Set([
  "monthly",
  "monthly_essential",
  "monthly_pro",
  "monthly_elite",
  "monthly_unlimited",
]);
const ACCEPTED_EXTS = [
  ".pdf",
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
  ".txt",
  ".csv",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
];
const ACCEPT_ATTR = ["image/*", ...ACCEPTED_EXTS].join(",");

function useIsSmallScreen(breakpoint = 1024) {
  const getInitial = () =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false;
  const [isSmall, setIsSmall] = useState(getInitial());

  useEffect(() => {
    const onResize = () => setIsSmall(getInitial());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return isSmall;
}

function normalizePageCountInput(value) {
  const n = Number(String(value ?? "").replace(/[^0-9]/g, ""));
  if (!Number.isFinite(n) || n < 1) return 0;
  return Math.trunc(n);
}

function formatBytes(bytes) {
  const n = Math.max(0, Number(bytes || 0));
  if (n >= 1024 * 1024) {
    return `${(n / (1024 * 1024)).toFixed(n >= 10 * 1024 * 1024 ? 1 : 2)} MB`;
  }
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${n} B`;
}

function formatDuration(milliseconds) {
  const totalSeconds = Math.max(0, Math.floor(Number(milliseconds || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (!minutes) return `${seconds}s`;
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
}

function formatUploadSpeed(bytesPerSecond) {
  const n = Math.max(0, Number(bytesPerSecond || 0));
  if (!n) return "Calculating speed…";
  return `${formatBytes(n)}/s`;
}

function getFileExtension(fileName = "") {
  const name = String(fileName || "").toLowerCase();
  const index = name.lastIndexOf(".");
  return index >= 0 ? name.slice(index + 1) : "";
}

function isPdfFile(file) {
  return (
    getFileExtension(file?.name) === "pdf" ||
    String(file?.type || "").toLowerCase() === "application/pdf"
  );
}

function isImageFile(file) {
  const ext = getFileExtension(file?.name);
  return (
    String(file?.type || "").toLowerCase().startsWith("image/") ||
    ["png", "jpg", "jpeg", "webp"].includes(ext)
  );
}

function isTextFile(file) {
  const ext = getFileExtension(file?.name);
  return (
    String(file?.type || "").toLowerCase().startsWith("text/") ||
    ["txt", "csv"].includes(ext)
  );
}

function isAcceptedFile(file) {
  if (!file) return false;
  const ext = getFileExtension(file.name);
  return ACCEPTED_EXTS.includes(`.${ext}`) || String(file.type || "").startsWith("image/");
}

async function estimatePdfPageCount(file) {
  const buffer = await file.arrayBuffer();
  const text = new TextDecoder("latin1").decode(buffer);
  const matches = text.match(/\/Type\s*\/Page\b/g);
  return Array.isArray(matches) ? matches.length : 0;
}

async function estimateTextPageCount(file) {
  const text = await file.text();
  return Math.max(1, Math.ceil(String(text || "").length / 3000));
}

async function getFrontendPageInfo(file) {
  if (!file) {
    return {
      detectedPageCount: 0,
      pageCountStatus: "unknown",
      pageCountSource: "missing_file",
      manualRequired: false,
    };
  }

  try {
    if (isPdfFile(file)) {
      const pages = await estimatePdfPageCount(file);
      if (pages > 0) {
        return {
          detectedPageCount: pages,
          pageCountStatus: "provided",
          pageCountSource: "frontend_pdf_soft_count",
          manualRequired: false,
        };
      }
      return {
        detectedPageCount: 0,
        pageCountStatus: "manual_required",
        pageCountSource: "frontend_pdf_count_failed",
        manualRequired: true,
      };
    }

    if (isImageFile(file)) {
      return {
        detectedPageCount: 1,
        pageCountStatus: "provided",
        pageCountSource: "frontend_image_one_page",
        manualRequired: false,
      };
    }

    if (isTextFile(file)) {
      const pages = await estimateTextPageCount(file);
      return {
        detectedPageCount: pages,
        pageCountStatus: "estimated",
        pageCountSource: "frontend_text_estimate",
        manualRequired: false,
      };
    }

    return {
      detectedPageCount: 0,
      pageCountStatus: "manual_required",
      pageCountSource: "frontend_not_supported_for_page_count",
      manualRequired: true,
    };
  } catch (err) {
    console.warn("Contract Insight page count check failed:", err);
    return {
      detectedPageCount: 0,
      pageCountStatus: "manual_required",
      pageCountSource: "frontend_page_count_error",
      manualRequired: true,
    };
  }
}

function parseDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function monthlyPlanIsActive(data = {}) {
  const planType = String(data.plan_type || data.planType || "").trim().toLowerCase();
  if (!MONTHLY_PLAN_TYPES.has(planType)) return false;
  if (data.is_active === false || data.isActive === false) return false;

  const candidates = [
    data.current_period_end,
    data.currentPeriodEnd,
    data.subscription_current_period_end,
    data.subscriptionCurrentPeriodEnd,
    data.expires_at,
    data.expiresAt,
  ];

  for (const value of candidates) {
    if (!value) continue;
    const date = parseDate(value);
    if (!date) continue;
    return date.getTime() > Date.now();
  }

  return true;
}

function readPackageRemaining(pkg) {
  if (typeof pkg === "number" && Number.isFinite(pkg)) return Math.max(0, Math.trunc(pkg));
  if (!pkg || typeof pkg !== "object") return 0;

  const candidates = [
    pkg.remaining_reports,
    pkg.remaining_report,
    pkg.remaining_docs,
    pkg.remaining_documents,
    pkg.remaining,
    pkg.count,
    pkg.qty,
    pkg.quantity,
    pkg.reports,
    pkg.docs,
  ];

  for (const value of candidates) {
    const n = Number(value);
    if (Number.isFinite(n)) return Math.max(0, Math.trunc(n));
  }
  return 0;
}

function readPackageTier(pkg, fallbackKey = "") {
  if (pkg && typeof pkg === "object") {
    const candidates = [
      pkg.page_tier,
      pkg.pageTier,
      pkg.max_pages,
      pkg.maxPages,
      pkg.pages,
      pkg.tier,
    ];
    for (const value of candidates) {
      const n = Number(value);
      if (Number.isFinite(n) && n > 0) return Math.trunc(n);
    }
  }

  const match = String(fallbackKey || "").match(/\d+/);
  return match ? Number(match[0]) : 0;
}

function normalizeContractInsightPackages(bucket) {
  const counts = new Map();
  const raw = bucket && typeof bucket === "object"
    ? bucket.contract_insight_page_packages
    : null;

  if (Array.isArray(raw)) {
    raw.forEach((pkg) => {
      const tier = readPackageTier(pkg);
      if (!CONTRACT_INSIGHT_PAGE_TIERS.includes(tier)) return;
      counts.set(tier, (counts.get(tier) || 0) + readPackageRemaining(pkg));
    });
  } else if (raw && typeof raw === "object") {
    Object.entries(raw).forEach(([key, value]) => {
      const tier = readPackageTier(value, key);
      if (!CONTRACT_INSIGHT_PAGE_TIERS.includes(tier)) return;
      counts.set(tier, (counts.get(tier) || 0) + readPackageRemaining(value));
    });
  }

  return CONTRACT_INSIGHT_PAGE_TIERS.map((tier) => ({
    page_tier: tier,
    remaining: Math.max(0, Number(counts.get(tier) || 0)),
  }));
}

function mergeContractInsightPackages(monthlyRows, addonRows, monthlyActive) {
  return CONTRACT_INSIGHT_PAGE_TIERS.map((tier) => {
    const monthlyRemaining = monthlyActive
      ? Number((monthlyRows || []).find((row) => row.page_tier === tier)?.remaining || 0)
      : 0;
    const addonRemaining = Number(
      (addonRows || []).find((row) => row.page_tier === tier)?.remaining || 0
    );

    return {
      page_tier: tier,
      monthly_remaining: Math.max(0, monthlyRemaining),
      addon_remaining: Math.max(0, addonRemaining),
      remaining: Math.max(0, monthlyRemaining) + Math.max(0, addonRemaining),
    };
  });
}

function getLowestAvailablePackage(rows) {
  return [...(rows || [])]
    .filter((row) => Number(row?.remaining || 0) > 0)
    .sort((a, b) => Number(a.page_tier || 0) - Number(b.page_tier || 0))[0] || null;
}

function getSmallestFittingPackage(rows, pages) {
  const requestedPages = Math.max(0, Number(pages || 0));
  return [...(rows || [])]
    .filter(
      (row) =>
        Number(row.remaining || 0) > 0 &&
        Number(row.page_tier || 0) >= requestedPages
    )
    .sort((a, b) => Number(a.page_tier || 0) - Number(b.page_tier || 0))[0] || null;
}

function getHighestAvailableTier(rows) {
  const available = (rows || []).filter((row) => Number(row.remaining || 0) > 0);
  if (!available.length) return 0;
  return Math.max(...available.map((row) => Number(row.page_tier || 0)));
}

function useObjectPreview(file) {
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!file || !isImageFile(file)) {
      setPreview(null);
      return undefined;
    }

    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  return preview;
}

function isBillingLikeError(error) {
  const status = Number(error?.status || error?.statusCode || error?.response?.status || 0);
  const text = String(error?.message || error?.error || error?.response?.error || error?.response?.message || "").toLowerCase();
  return (
    status === 402 ||
    text.includes("billing") ||
    text.includes("package") ||
    text.includes("credit") ||
    text.includes("payment") ||
    text.includes("subscription")
  );
}

function getFriendlySystemMessage(error, fallback = "Something went wrong while preparing your contract analysis. Please try again later.") {
  if (isBillingLikeError(error)) {
    return "Your account does not have an available Contract Insight package for this analysis. Please review your packages on Billing.";
  }
  return fallback;
}

export default function ContractInsightPage() {
  const navigate = useNavigate();
  const isSmall = useIsSmallScreen();
  const onIOSApp = isIOSApp();

  const fileInputRef = useRef(null);
  const idTokenRef = useRef(null);
  const uploadProtectionRef = useRef(false);

  const [idToken, setIdToken] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [schemaId, setSchemaId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [pageInfo, setPageInfo] = useState({
    detectedPageCount: 0,
    manualPageCount: 0,
    pageCountStatus: "unknown",
    pageCountSource: "",
    manualRequired: false,
    checking: false,
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [notice, setNotice] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [jobId, setJobId] = useState(null);

  const [billingSnapshot, setBillingSnapshot] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState("");
  const [packageOpen, setPackageOpen] = useState(true);
  const [selectedPackageTier, setSelectedPackageTier] = useState(null);
  const [confirmedHigherPackageKey, setConfirmedHigherPackageKey] = useState("");

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadPhase, setUploadPhase] = useState("");
  const [uploadProgress, setUploadProgress] = useState({
    percent: 0,
    loadedBytes: 0,
    totalBytes: 0,
    elapsedMs: 0,
    averageBytesPerSecond: 0,
  });

  const imagePreviewUrl = useObjectPreview(selectedFile);
  const declaredPageCount = pageInfo.manualRequired
    ? normalizePageCountInput(pageInfo.manualPageCount)
    : Math.max(0, Number(pageInfo.detectedPageCount || 0));

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!uploadProtectionRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        idTokenRef.current = null;
        setIdToken(null);
        setIsLoggedIn(false);
        setBillingSnapshot(null);
        return;
      }

      try {
        const token = await user.getIdToken();
        idTokenRef.current = token;
        setIdToken(token);
        setIsLoggedIn(true);
        fetchBillingSnapshot(token);
      } catch (err) {
        console.error("Failed to fetch Firebase ID token for Contract Insight:", err);
        idTokenRef.current = null;
        setIdToken(null);
        setIsLoggedIn(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const getFreshAuthToken = async ({ forceRefresh = false } = {}) => {
    const user = getAuth()?.currentUser;
    if (!user) throw new Error("Please log in to use Contract Insight.");
    const token = await user.getIdToken(forceRefresh);
    idTokenRef.current = token;
    setIdToken(token);
    setIsLoggedIn(true);
    return token;
  };

  const fetchBillingSnapshot = async (tokenOverride) => {
    const token = tokenOverride || idTokenRef.current || idToken;
    if (!token) {
      setBillingSnapshot(null);
      return null;
    }

    setBillingLoading(true);
    setBillingError("");

    try {
      const response = await fetch(`${HOST}/user/billing`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || data?.message || `HTTP ${response.status}`);
      }
      setBillingSnapshot(data || {});
      return data || {};
    } catch (err) {
      console.error("Contract Insight billing fetch error:", err);
      setBillingSnapshot(null);
      setBillingError("Unable to load your Contract Insight packages right now. Please refresh or visit Billing.");
      return null;
    } finally {
      setBillingLoading(false);
    }
  };

  const packageInfo = useMemo(() => {
    const outer = billingSnapshot || {};
    const billing = outer.billing && typeof outer.billing === "object" ? outer.billing : outer;
    const monthlyActive = monthlyPlanIsActive(billing);
    const monthlyEntitlements =
      billing.monthly_entitlements || billing.monthlyEntitlements || {};
    const addonEntitlements =
      billing.addon_entitlements ||
      billing.addonEntitlements ||
      billing.purchased_addons ||
      {};

    const monthlyPackages = normalizeContractInsightPackages(monthlyEntitlements);
    const addonPackages = normalizeContractInsightPackages(addonEntitlements);
    const combinedPackages = mergeContractInsightPackages(
      monthlyPackages,
      addonPackages,
      monthlyActive
    );
    const availablePackages = combinedPackages
      .filter((row) => Number(row.remaining || 0) > 0)
      .sort((a, b) => Number(a.page_tier || 0) - Number(b.page_tier || 0));

    return {
      monthlyActive,
      monthlyPackages,
      addonPackages,
      combinedPackages,
      availablePackages,
      highestTier: getHighestAvailableTier(combinedPackages),
    };
  }, [billingSnapshot]);

  const packageSignature = useMemo(
    () =>
      packageInfo.availablePackages
        .map(
          (row) =>
            `${row.page_tier}:${row.monthly_remaining}:${row.addon_remaining}:${row.remaining}`
        )
        .join("|"),
    [packageInfo.availablePackages]
  );

  useEffect(() => {
    if (billingLoading) return;
    const available = packageInfo.availablePackages || [];
    if (!available.length) {
      if (selectedPackageTier !== null) setSelectedPackageTier(null);
      return;
    }

    const stillAvailable = available.some(
      (row) => Number(row.page_tier || 0) === Number(selectedPackageTier || 0)
    );
    if (!stillAvailable) {
      const lowest = getLowestAvailablePackage(available);
      setSelectedPackageTier(lowest ? Number(lowest.page_tier || 0) : null);
      setConfirmedHigherPackageKey("");
    }
  }, [packageSignature, billingLoading, selectedPackageTier]);

  const selectedPackage = useMemo(
    () =>
      packageInfo.availablePackages.find(
        (row) => Number(row.page_tier || 0) === Number(selectedPackageTier || 0)
      ) || null,
    [packageInfo.availablePackages, selectedPackageTier]
  );

  const selectedPackagePageLimit = Number(selectedPackage?.page_tier || 0);
  const selectedPackageLimitError = useMemo(() => {
    if (!selectedPackage) return "Please select one of your available Contract Insight packages.";
    if (!selectedFile || declaredPageCount <= 0) return "";
    if (declaredPageCount <= selectedPackagePageLimit) return "";

    const higherAvailable = packageInfo.availablePackages.some(
      (row) =>
        Number(row.remaining || 0) > 0 &&
        Number(row.page_tier || 0) >= declaredPageCount
    );

    return `Your selected ${selectedPackagePageLimit.toLocaleString()}-page package allows up to ${selectedPackagePageLimit.toLocaleString()} declared pages. Your contract declares ${declaredPageCount.toLocaleString()} pages. ${
      higherAvailable
        ? "Please select an available higher package."
        : "No available package can cover this declared page count."
    }`;
  }, [
    selectedPackage,
    selectedFile,
    declaredPageCount,
    selectedPackagePageLimit,
    packageInfo.availablePackages,
  ]);

  const recommendedLowerPackage = useMemo(() => {
    if (!selectedPackage || !selectedFile || declaredPageCount <= 0) return null;
    const smallest = getSmallestFittingPackage(
      packageInfo.availablePackages,
      declaredPageCount
    );
    if (!smallest) return null;
    return Number(smallest.page_tier || 0) < selectedPackagePageLimit ? smallest : null;
  }, [
    selectedPackage,
    selectedFile,
    declaredPageCount,
    packageInfo.availablePackages,
    selectedPackagePageLimit,
  ]);

  const higherPackageConfirmationKey = recommendedLowerPackage
    ? `${selectedPackagePageLimit}:${recommendedLowerPackage.page_tier}:${declaredPageCount}`
    : "";
  const higherPackageConfirmationRequired = Boolean(
    recommendedLowerPackage && confirmedHigherPackageKey !== higherPackageConfirmationKey
  );

  const selectPackageTier = (tier) => {
    const selected = Number(tier || 0);
    const available = packageInfo.availablePackages.some(
      (row) =>
        Number(row.page_tier || 0) === selected && Number(row.remaining || 0) > 0
    );
    if (!available) return;
    setSelectedPackageTier(selected);
    setConfirmedHigherPackageKey("");
    setFieldErrors((prev) => ({ ...prev, package: "" }));
  };

  const handleFileSelection = async (file) => {
    setNotice(null);
    setFieldErrors((prev) => ({ ...prev, file: "", pages: "" }));

    if (!file) {
      setSelectedFile(null);
      setPageInfo({
        detectedPageCount: 0,
        manualPageCount: 0,
        pageCountStatus: "unknown",
        pageCountSource: "",
        manualRequired: false,
        checking: false,
      });
      return;
    }

    if (!isAcceptedFile(file)) {
      setSelectedFile(null);
      setFieldErrors((prev) => ({
        ...prev,
        file: "This file type is not supported. Please select PDF, DOC, DOCX, TXT, CSV, XLS, XLSX, PNG, JPG, JPEG, or WEBP.",
      }));
      return;
    }

    if (Number(file.size || 0) <= 0) {
      setSelectedFile(null);
      setFieldErrors((prev) => ({ ...prev, file: "The selected file is empty." }));
      return;
    }

    if (Number(file.size || 0) > CONTRACT_INSIGHT_MAX_FILE_BYTES) {
      setSelectedFile(null);
      setFieldErrors((prev) => ({
        ...prev,
        file: `The selected file is larger than the ${CONTRACT_INSIGHT_MAX_FILE_MB} MB limit.`,
      }));
      return;
    }

    setSelectedFile(file);
    setPageInfo((prev) => ({ ...prev, checking: true }));
    const info = await getFrontendPageInfo(file);
    setPageInfo({
      detectedPageCount: Number(info.detectedPageCount || 0),
      manualPageCount: 0,
      pageCountStatus: info.pageCountStatus || "unknown",
      pageCountSource: info.pageCountSource || "",
      manualRequired: Boolean(info.manualRequired),
      checking: false,
    });
    setConfirmedHigherPackageKey("");
    setNotice({
      type: "ok",
      msg: "File attached. Confirm the page count, select the package you want to use, agree to the terms, and click Analyze contract.",
    });
  };

  const focusValidationTarget = (targetId, preferredSelectors = []) => {
    window.setTimeout(() => {
      const target = document.getElementById(targetId);
      if (!target) return;

      const collapsedToggle = target.querySelector(
        'button[data-contract-step-toggle="true"][aria-expanded="false"]'
      );
      collapsedToggle?.click?.();

      window.setTimeout(() => {
        target.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        const selectors = [
          ...preferredSelectors,
          '[aria-invalid="true"]',
          'input:not([type="hidden"])',
          "select",
          "textarea",
          "button",
        ];

        const focusable = selectors
          .map((selector) => target.querySelector(selector))
          .find((element) => element && !element.disabled);

        window.setTimeout(() => {
          focusable?.focus?.({ preventScroll: true });
        }, 240);
      }, collapsedToggle ? 90 : 0);
    }, 20);
  };

  const validateBeforeRun = () => {
    const errors = {};
    if (schemaId === null) errors.schema = "Please select your contract type.";
    if (!selectedPackage) errors.package = "Please select one of your available Contract Insight packages.";
    if (!selectedFile) errors.file = "Please upload your contract file.";
    if (pageInfo.checking) errors.pages = "Please wait while the page count is checked.";
    if (selectedFile && pageInfo.manualRequired && declaredPageCount <= 0) {
      errors.pages = "Please enter the contract page count.";
    }
    if (selectedPackageLimitError) errors.package = selectedPackageLimitError;
    if (higherPackageConfirmationRequired) {
      errors.package = `Your contract also fits your available ${Number(
        recommendedLowerPackage?.page_tier || 0
      ).toLocaleString()}-page package. Select that package or confirm that you want to use the larger selected package.`;
    }
    if (!agreedToTerms) {
      errors.terms = "Please agree to the Terms of Use and Privacy Policy.";
    }

    setFieldErrors(errors);

    const order = [
      ["schema", "contract-insight-schema"],
      ["package", "contract-insight-package"],
      ["file", "contract-insight-file"],
      ["pages", "contract-insight-file"],
      ["terms", "contract-insight-terms"],
    ];
    const first = order.find(([key]) => errors[key]);

    if (first?.[0] === "package") {
      setPackageOpen(true);
    }

    if (first) {
      const preferredSelectors =
        first[0] === "schema"
          ? ['[role="combobox"]', "select", 'input:not([type="hidden"])']
          : first[0] === "package"
            ? ['input[type="radio"]', "button"]
            : first[0] === "file" || first[0] === "pages"
              ? ['input[type="file"]', 'input[inputmode="numeric"]', "button"]
              : ['input[type="checkbox"]'];

      focusValidationTarget(first[1], preferredSelectors);
    }

    return !first;
  };

  const buildPayload = () => ({
    language: "auto",
    job_name: selectedFile?.name || "Contract Insight",
    schema_id: schemaId === null ? null : schemaId,
    schemaId: schemaId === null ? null : schemaId,
    contract_profile: {
      schema_id: schemaId === null ? null : schemaId,
      schemaId: schemaId === null ? null : schemaId,
      selection_source: "frontend_user_selection",
    },

    // Keep this payload intentionally small. Package validation is finalized server-side.
    contract_insight_package: {
      package_pages: selectedPackagePageLimit || null,
      page_limit: selectedPackagePageLimit || null,
      selected_page_tier: selectedPackagePageLimit || null,
      selectedPageTier: selectedPackagePageLimit || null,
      selected_by_user: Boolean(selectedPackage),
      selectedByUser: Boolean(selectedPackage),
      selection_source: "frontend_user_selection",
      selectionSource: "frontend_user_selection",
    },
    file_package: {
      total_file_count: selectedFile ? 1 : 0,
      requested_file_count: selectedFile ? 1 : 0,
      total_upload_bytes: Number(selectedFile?.size || 0),
      total_upload_mb: Number((Number(selectedFile?.size || 0) / (1024 * 1024)).toFixed(2)),
      total_page_count: declaredPageCount,
      known_page_count: declaredPageCount,
      detected_page_count: Number(pageInfo.detectedPageCount || 0),
      user_entered_page_count: pageInfo.manualRequired
        ? normalizePageCountInput(pageInfo.manualPageCount)
        : 0,
      declared_total_page_count: declaredPageCount,
      page_count_status: pageInfo.manualRequired && declaredPageCount > 0
        ? "user_provided"
        : pageInfo.pageCountStatus,
      page_count_source: pageInfo.manualRequired && declaredPageCount > 0
        ? "manual_user_entry"
        : pageInfo.pageCountSource,
      selected_contract_insight_page_tier: selectedPackagePageLimit || null,
      per_file_max_mb: CONTRACT_INSIGHT_MAX_FILE_MB,
    },
    billing_package: {
      product: "contract_insight",
      requested_page_count: declaredPageCount,
      known_page_count: declaredPageCount,
      declared_total_page_count: declaredPageCount,
      detected_page_count: Number(pageInfo.detectedPageCount || 0),
      user_entered_page_count: pageInfo.manualRequired
        ? normalizePageCountInput(pageInfo.manualPageCount)
        : 0,
      requested_file_count: selectedFile ? 1 : 0,
      selected_page_tier: selectedPackagePageLimit || null,
      selectedPageTier: selectedPackagePageLimit || null,
      selected_by_user: Boolean(selectedPackage),
      selectedByUser: Boolean(selectedPackage),
      selection_source: "frontend_user_selection",
      selectionSource: "frontend_user_selection",
    },
    accepted_terms: Boolean(agreedToTerms),
  });

  const handleUploadEvent = (event = {}) => {
    const phase = String(event.phase || "");
    if (phase === "requesting_upload_url") {
      setUploadPhase("Preparing your secure upload…");
    } else if (phase === "uploading_file") {
      setUploadPhase(`Uploading ${event.fileName || "your contract"}…`);
    } else if (phase === "confirming_upload") {
      setUploadPhase("Confirming your uploaded contract…");
    } else if (phase === "completed") {
      setUploadPhase("Upload complete. Starting your contract analysis…");
    }

    setUploadProgress((prev) => ({
      ...prev,
      percent: Math.max(0, Math.min(100, Number(event.percent ?? prev.percent ?? 0))),
      loadedBytes: Math.max(0, Number(event.loadedBytes ?? prev.loadedBytes ?? 0)),
      totalBytes: Math.max(
        0,
        Number(event.totalBytes ?? event.sizeBytes ?? prev.totalBytes ?? selectedFile?.size ?? 0)
      ),
      elapsedMs: Math.max(0, Number(event.elapsedMs ?? prev.elapsedMs ?? 0)),
      averageBytesPerSecond: Math.max(
        0,
        Number(event.averageBytesPerSecond ?? prev.averageBytesPerSecond ?? 0)
      ),
    }));
  };

  const goToStatusPage = (id, tokenForStatus = "") => {
    if (!id) return;
    const statusToken = tokenForStatus || idTokenRef.current || idToken || "";
    try {
      sessionStorage.setItem(`contractInsightStatus:${id}`, "allowed");
    } catch (_) {
      // Route state below still supports the normal handoff.
    }
    navigate(`/contract/status?jobId=${encodeURIComponent(id)}`, {
      replace: true,
      state: {
        fromContractInsight: true,
        jobId: id,
        idToken: statusToken,
        hasIdToken: Boolean(statusToken),
      },
    });
  };

  const handleAnalyze = async () => {
    setNotice(null);
    setUploadProgress({
      percent: 0,
      loadedBytes: 0,
      totalBytes: Number(selectedFile?.size || 0),
      elapsedMs: 0,
      averageBytesPerSecond: 0,
    });

    let freshToken;
    try {
      freshToken = await getFreshAuthToken({ forceRefresh: true });
    } catch {
      navigate("/login", { state: { from: "/contract/start" } });
      return;
    }

    if (!validateBeforeRun()) return;

    setBusy(true);
    setUploadModalOpen(true);
    setUploadPhase("Preparing your contract analysis…");
    uploadProtectionRef.current = true;

    let createdJobId = null;
    let analyzeSubmitted = false;

    try {
      const payload = buildPayload();
      const created = await createContractInsightJob(payload, { idToken: freshToken });
      createdJobId = created?.job_id || created?.jobId || created?.id;
      if (!createdJobId) throw new Error("Backend did not return a Contract Insight job id.");
      setJobId(createdJobId);

      setUploadPhase("Preparing your secure upload…");
      await uploadContractInsightFile(
        createdJobId,
        selectedFile,
        {
          original_name: selectedFile.name,
          originalName: selectedFile.name,
          content_type: selectedFile.type || "application/octet-stream",
          contentType: selectedFile.type || "application/octet-stream",
          size_bytes: selectedFile.size,
          sizeBytes: selectedFile.size,
          page_count: declaredPageCount,
          pageCount: declaredPageCount,
          detected_page_count: Number(pageInfo.detectedPageCount || 0),
          detectedPageCount: Number(pageInfo.detectedPageCount || 0),
          manual_page_count: pageInfo.manualRequired
            ? normalizePageCountInput(pageInfo.manualPageCount)
            : 0,
          manualPageCount: pageInfo.manualRequired
            ? normalizePageCountInput(pageInfo.manualPageCount)
            : 0,
          manual_page_count_required: Boolean(pageInfo.manualRequired),
          manualPageCountRequired: Boolean(pageInfo.manualRequired),
          page_count_status: pageInfo.manualRequired ? "user_provided" : pageInfo.pageCountStatus,
          pageCountStatus: pageInfo.manualRequired ? "user_provided" : pageInfo.pageCountStatus,
          page_count_source: pageInfo.manualRequired ? "manual_user_entry" : pageInfo.pageCountSource,
          pageCountSource: pageInfo.manualRequired ? "manual_user_entry" : pageInfo.pageCountSource,
          requested_page_count: declaredPageCount,
          requestedPageCount: declaredPageCount,
          declared_total_page_count: declaredPageCount,
          declaredTotalPageCount: declaredPageCount,
          selected_page_tier: selectedPackagePageLimit,
          selectedPageTier: selectedPackagePageLimit,
          schema_id: schemaId === null ? null : schemaId,
          schemaId: schemaId === null ? null : schemaId,
        },
        {
          idToken: freshToken,
          onUploadEvent: handleUploadEvent,
        }
      );

      setUploadPhase("Upload complete. Starting your contract review…");
      analyzeSubmitted = true;
      const started = await startContractInsightRun(createdJobId, payload, {
        idToken: freshToken,
      });
      const startedJobId = started?.job_id || started?.jobId || createdJobId;
      if (!startedJobId) throw new Error("Backend did not return a Contract Insight job id.");

      uploadProtectionRef.current = false;
      setUploadPhase("Opening the status page…");
      goToStatusPage(startedJobId, freshToken);
    } catch (err) {
      console.error("Contract Insight launcher error:", err);
      uploadProtectionRef.current = false;
      setUploadModalOpen(false);
      setBusy(false);
      const billingLike = isBillingLikeError(err);
      setNotice({
        type: billingLike ? "billing" : "error",
        msg: getFriendlySystemMessage(err),
      });

      if (createdJobId && !analyzeSubmitted) {
        abandonContractInsightJob(
          createdJobId,
          {
            reason: "frontend_pre_analysis_failure",
            failureStage: err?.failureStage || err?.uploadStage || "pre_analysis_failure",
            fileName: selectedFile?.name || "",
            elapsedMs: Number(err?.elapsedMs || err?.timings?.total_ms || 0),
          },
          { idToken: freshToken }
        ).catch((abandonError) => {
          console.warn("Unable to abandon failed Contract Insight job:", abandonError);
        });
      }
    }
  };

  const analyzeDisabled = Boolean(
    busy || (isLoggedIn && billingLoading)
  );

  return (
    <div
      className="contract-insight-page-root"
      style={{ ...styles.outerWrap, ...(isSmall ? styles.outerWrapMobile : null) }}
    >
      <style>{`
        .contract-insight-page-root * { box-sizing: border-box; }
        .contract-insight-page-root button,
        .contract-insight-page-root input,
        .contract-insight-page-root select,
        .contract-insight-page-root textarea { font: inherit; }
        .contract-insight-page-root a { color: #2563EB; font-weight: 900; text-decoration: none; }
        .contract-insight-page-root a:hover { text-decoration: underline; }
        .contract-insight-page-root button:focus-visible,
        .contract-insight-page-root input:focus-visible,
        .contract-insight-page-root select:focus-visible {
          outline: 3px solid rgba(37,99,235,0.22);
          outline-offset: 2px;
        }
        @keyframes ci-spin { to { transform: rotate(360deg); } }

        html, body, #root {
          max-width: 100%;
          overflow-x: hidden;
        }

        .contract-insight-page-root,
        .contract-insight-page-root *,
        .contract-insight-feature-grid,
        .contract-insight-page-grid,
        .contract-insight-workflow-panel {
          min-width: 0;
          max-width: 100%;
        }

        @media (max-width: 1023px) {
          .contract-insight-page-root {
            padding: 14px 12px 34px !important;
          }
        }

        @media (max-width: 640px) {
          .contract-insight-step-header {
            align-items: flex-start !important;
          }

          .contract-insight-step-body {
            padding-top: 13px !important;
          }
        }
      `}</style>

      <header style={styles.pageHeader}>
        <div style={styles.pageHeaderText}>
          <div style={{ ...styles.eyebrow, ...(isSmall ? styles.eyebrowMobile : null) }}>Contract Insight</div>
          <h1 style={{ ...styles.h1, ...(isSmall ? styles.h1Mobile : null) }}>
            Analyze your contract
          </h1>
          <p style={{ ...styles.subtitle, ...(isSmall ? styles.subtitleMobile : null) }}>
            Upload one contract and receive a structured report with key terms,
            risk callouts, and a downloadable summary.
          </p>
        </div>
      </header>

      <div className="contract-insight-feature-grid" style={{ ...styles.featureGrid, ...(isSmall ? styles.featureGridMobile : null) }}>
        <FeatureTile icon="▤" title="Key Terms Mapped" text="Extract and organize important terms and obligations." />
        <FeatureTile icon="◇" title="Risk Callouts" text="Surface potential risks, gaps, and unusual clauses." />
        <FeatureTile icon="↓" title="Downloadable Report" text="Receive a clean report saved to your account." />
      </div>

      <div className="contract-insight-page-grid" style={{ ...styles.pageGrid, ...(isSmall ? styles.pageGridMobile : null) }}>
        <main style={styles.mainColumn}>
          <section className="contract-insight-workflow-panel" style={styles.workflowPanel}>
            <StepSection
              number="1"
              title="Choose contract type"
              id="contract-insight-schema"
              required
            >
              <div style={styles.schemaWrap}>
                <ContractSchemaSelector
                  schemaId={schemaId}
                  onSchemaChange={(value) => {
                    setSchemaId(value);
                    setFieldErrors((prev) => ({ ...prev, schema: "" }));
                  }}
                  error={fieldErrors.schema}
                />
              </div>
              <div style={styles.helperLine}>
                <span style={styles.infoDot}>i</span>
                <span>
                  Choose the closest match, or use <strong>Other / Not sure</strong> for auto-detection.
                </span>
              </div>
              {fieldErrors.schema ? <InlineError>{fieldErrors.schema}</InlineError> : null}
            </StepSection>

            <StepSection
              number="2"
              title="Select your package"
              id="contract-insight-package"
              required
            >
              <ContractInsightPackageCard
                packageInfo={packageInfo}
                selectedPackage={selectedPackage}
                selectedPackageTier={selectedPackageTier}
                declaredPageCount={declaredPageCount}
                selectedPackageLimitError={selectedPackageLimitError}
                recommendedLowerPackage={recommendedLowerPackage}
                higherPackageConfirmationRequired={higherPackageConfirmationRequired}
                loading={billingLoading}
                error={billingError || fieldErrors.package}
                expanded={packageOpen}
                onToggle={() => setPackageOpen((prev) => !prev)}
                onRefresh={() => fetchBillingSnapshot()}
                onBilling={() => navigate("/billing")}
                onSelectPackage={selectPackageTier}
                onUseRecommendedPackage={() =>
                  selectPackageTier(Number(recommendedLowerPackage?.page_tier || 0))
                }
                onConfirmSelectedPackage={() => {
                  setConfirmedHigherPackageKey(higherPackageConfirmationKey);
                  setFieldErrors((prev) => ({ ...prev, package: "" }));
                }}
              />
            </StepSection>

            <StepSection
              number="3"
              title="Upload your contract"
              id="contract-insight-file"
              required
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_ATTR}
                style={{ display: "none" }}
                onChange={(event) => {
                  const file = event.target.files?.[0] || null;
                  handleFileSelection(file);
                  event.target.value = "";
                }}
              />

              <div
                style={styles.dropZone}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "copy";
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  if (busy) return;
                  handleFileSelection(event.dataTransfer.files?.[0] || null);
                }}
              >
                <div style={styles.dropIcon}>▯</div>
                <div style={styles.dropHeadline}>Drag and drop your contract here</div>
                <div style={styles.dropOr}>or</div>
                <button
                  type="button"
                  style={styles.primaryButton}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={busy}
                >
                  Choose file
                </button>
                <div style={styles.fileHint}>
                  <div style={styles.fileHintTitle}>
                    Best results: upload a clear, complete PDF.
                  </div>
                  <div style={styles.fileHintMeta}>
                    One contract • {CONTRACT_INSIGHT_MAX_FILE_MB} MB maximum • Up to 250 pages
                  </div>
                </div>
              </div>

              {selectedFile ? (
                <div style={styles.selectedFileRow}>
                  <div style={styles.fileIcon}>{getFileExtension(selectedFile.name).toUpperCase() || "FILE"}</div>
                  <div style={styles.selectedFileTextWrap}>
                    <div style={styles.selectedFileName}>{selectedFile.name}</div>
                    <div style={styles.selectedFileMeta}>
                      {formatBytes(selectedFile.size)}
                      {pageInfo.checking
                        ? " • Checking pages…"
                        : declaredPageCount > 0
                          ? ` • ${declaredPageCount.toLocaleString()} page${declaredPageCount === 1 ? "" : "s"}`
                          : " • Page count required"}
                    </div>
                  </div>
                  <button
                    type="button"
                    style={styles.removeButton}
                    onClick={() => handleFileSelection(null)}
                    disabled={busy}
                    aria-label="Remove selected contract"
                  >
                    ×
                  </button>
                </div>
              ) : null}

              {pageInfo.manualRequired && selectedFile ? (
                <label style={styles.manualPageField}>
                  <span style={styles.manualPageLabel}>Page count needed</span>
                  <span style={styles.manualPageHelp}>
                    We couldn’t detect the number of pages. Enter the total below.
                    A PDF is recommended for best results.
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    aria-label="Contract page count"
                    aria-required="true"
                    value={pageInfo.manualPageCount}
                    onChange={(event) => {
                      const value = event.target.value.replace(/[^0-9]/g, "");
                      setPageInfo((prev) => ({ ...prev, manualPageCount: value }));
                      setConfirmedHigherPackageKey("");
                      setFieldErrors((prev) => ({ ...prev, pages: "", package: "" }));
                    }}
                    style={styles.manualPageInput}
                    placeholder="Example: 42"
                  />
                </label>
              ) : null}

              {imagePreviewUrl ? (
                <div style={styles.previewWrap}>
                  <div style={styles.previewTitle}>Quick preview</div>
                  <img src={imagePreviewUrl} alt={selectedFile?.name || "Contract preview"} style={styles.previewImage} />
                </div>
              ) : null}

              {fieldErrors.file ? <InlineError>{fieldErrors.file}</InlineError> : null}
              {fieldErrors.pages ? <InlineError>{fieldErrors.pages}</InlineError> : null}
            </StepSection>

            <StepSection
              number="4"
              title="Review & analyze"
              id="contract-insight-terms"
              required
            >
              <label style={styles.termsLabel}>
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(event) => {
                    setAgreedToTerms(event.target.checked);
                    setFieldErrors((prev) => ({ ...prev, terms: "" }));
                  }}
                  style={styles.termsCheckbox}
                />
                <span>
                  I agree to the <a href="/terms">Terms of Use</a> and{" "}
                  <a href="/privacy">Privacy Policy</a>.
                </span>
              </label>
              {fieldErrors.terms ? <InlineError>{fieldErrors.terms}</InlineError> : null}

              {notice ? (
                <div
                  style={{
                    ...styles.notice,
                    ...(notice.type === "ok"
                      ? styles.noticeOk
                      : notice.type === "billing"
                        ? styles.noticeBilling
                        : styles.noticeError),
                  }}
                >
                  <div>{notice.msg}</div>
                  {notice.type === "billing" ? (
                    <div style={styles.noticeSubtext}>
                      Review your package balance on the <a href="/billing">Billing page</a>.
                    </div>
                  ) : null}
                </div>
              ) : null}

              <button
                type="button"
                onClick={handleAnalyze}
                disabled={analyzeDisabled}
                style={{
                  ...styles.analyzeButton,
                  ...(analyzeDisabled ? styles.buttonDisabled : null),
                }}
              >
                {busy ? <span style={styles.buttonSpinner} /> : <span>✦</span>}
                {busy ? "Uploading contract…" : isLoggedIn ? "Analyze contract" : "Log in to analyze"}
              </button>
            </StepSection>
          </section>

          <div style={styles.guidanceStack}>
            <GuidanceRow icon="◌" title="Best results">
              Choose the closest contract type and upload the clearest complete copy available.
            </GuidanceRow>
            <GuidanceRow icon="▤" title="Package page limits">
              The declared page count must fit the package you select. The completed report will use the verified page count.
            </GuidanceRow>
            <GuidanceRow icon="◇" title="Privacy">
              Your contract is uploaded directly to secure cloud storage and the completed report is saved to your account.
            </GuidanceRow>
          </div>

          {onIOSApp ? (
            <p style={styles.iosNote}>
              To manage billing on iOS, visit <strong>www.asklawly.com</strong> in your browser.
            </p>
          ) : null}
        </main>

        <aside style={{ ...styles.sideColumn, ...(isSmall ? styles.sideColumnMobile : null) }}>
          <section style={styles.sidePanel}>
            <h3 style={styles.sideTitle}>How Contract Insight works</h3>
            <MiniStep number="1" title="Choose type" text="Select the contract category or choose Other / Not sure." />
            <MiniStep number="2" title="Select package" text="Choose one available page-tier package." />
            <MiniStep number="3" title="Upload contract" text="Upload one file and confirm its declared page count." />
            <MiniStep number="4" title="Generate report" text="Your analysis starts and you continue on the status page." last />
          </section>

          <section style={styles.sidePanel}>
            <h3 style={styles.sideTitle}>Contract package limits</h3>
            <p style={styles.sideCopy}>
              Available tiers are 25, 50, 75, 150, and 250 pages. Monthly credits are usable only while the monthly plan is active. Purchased add-ons do not expire.
            </p>
            <button type="button" style={styles.sideButton} onClick={() => navigate("/billing")}>
              Manage packages <span aria-hidden>›</span>
            </button>
          </section>
        </aside>
      </div>

      {uploadModalOpen ? (
        <UploadModal
          phase={uploadPhase}
          progress={uploadProgress}
          fileName={selectedFile?.name || ""}
          jobId={jobId}
        />
      ) : null}
    </div>
  );
}

function StepSection({
  number,
  title,
  id,
  children,
  required = false,
  defaultOpen = true,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const contentId = `contract-insight-step-${number}-content`;

  return (
    <section id={id} style={styles.stepSection}>
      <div
        className="contract-insight-step-header"
        style={styles.stepHeader}
      >
        <div style={styles.stepHeadingGroup}>
          <div style={styles.stepBadge}>{number}</div>

          <div style={styles.stepTitleBlock}>
            <div style={styles.stepTitleRow}>
              <h2 style={styles.h2}>{title}</h2>

              {required ? (
                <span style={styles.requiredPill}>Required</span>
              ) : null}
            </div>
          </div>
        </div>

        <button
          type="button"
          data-contract-step-toggle="true"
          onClick={() => setOpen((previous) => !previous)}
          aria-expanded={open}
          aria-controls={contentId}
          aria-label={`${open ? "Collapse" : "Expand"} ${title}`}
          style={styles.stepToggleButton}
        >
          <span aria-hidden="true">{open ? "−" : "+"}</span>
        </button>
      </div>

      <div
        id={contentId}
        className="contract-insight-step-body"
        hidden={!open}
        style={{
          ...styles.stepBody,
          ...(!open ? styles.stepBodyHidden : null),
        }}
      >
        {children}
      </div>
    </section>
  );
}

function InlineError({ children }) {
  return <div style={styles.inlineError}>{children}</div>;
}

function FeatureTile({ icon, title, text }) {
  return (
    <div style={styles.featureTile}>
      <div style={styles.featureIcon}>{icon}</div>
      <div>
        <h3 style={styles.featureTitle}>{title}</h3>
        <div style={styles.featureText}>{text}</div>
      </div>
    </div>
  );
}

function ContractInsightPackageCard({
  packageInfo,
  selectedPackage,
  selectedPackageTier,
  declaredPageCount,
  selectedPackageLimitError,
  recommendedLowerPackage,
  higherPackageConfirmationRequired,
  loading,
  error,
  expanded,
  onToggle,
  onRefresh,
  onBilling,
  onSelectPackage,
  onUseRecommendedPackage,
  onConfirmSelectedPackage,
}) {
  const available = packageInfo?.availablePackages || [];

  return (
    <div style={styles.packageCard}>
      <div style={styles.packageTop}>
        <button type="button" style={styles.packageHeaderButton} onClick={onToggle}>
          <span style={styles.packageToggle}>{expanded ? "−" : "+"}</span>
          <span>
            <span style={styles.packageTitle}>Select your Contract Insight package</span>
            <span style={styles.packageSub}>
              The lowest available package is selected by default. You may select another package you own.
            </span>
          </span>
        </button>
        <button
          type="button"
          style={styles.packageRefreshButton}
          onClick={onRefresh}
          disabled={loading}
        >
          {loading ? "Checking…" : "Refresh"}
        </button>
      </div>

      {expanded ? (
        <>
          {error ? <div style={styles.packageError}>{error}</div> : null}

          {!loading && !available.length ? (
            <div style={styles.packageEmpty}>
              No Contract Insight package is currently available on this account.
              <button type="button" style={styles.packageBillingButton} onClick={onBilling}>
                Manage packages
              </button>
            </div>
          ) : null}

          {available.length ? (
            <div style={styles.packageList} role="radiogroup" aria-label="Available Contract Insight packages">
              {available.map((row) => {
                const tier = Number(row.page_tier || 0);
                const selected = tier === Number(selectedPackageTier || 0);
                return (
                  <label
                    key={tier}
                    style={{
                      ...styles.packageRow,
                      ...(selected ? styles.packageRowSelected : null),
                    }}
                  >
                    <input
                      type="radio"
                      name="contract-insight-package"
                      value={tier}
                      checked={selected}
                      onChange={() => onSelectPackage(tier)}
                      style={styles.packageRadio}
                    />
                    <span style={styles.packageRowMain}>
                      <span style={styles.packageRowTitle}>{tier.toLocaleString()}-page package</span>
                      <span style={styles.packageRowMeta}>
                        Declared page limit: {tier.toLocaleString()} • One contract report
                      </span>
                      <span style={styles.packageBreakdown}>
                        Monthly: {Number(row.monthly_remaining || 0)} • Add-on: {Number(row.addon_remaining || 0)}
                      </span>
                    </span>
                    <strong style={styles.packageRemaining}>
                      {Number(row.remaining || 0)} report{Number(row.remaining || 0) === 1 ? "" : "s"} remaining
                    </strong>
                  </label>
                );
              })}
            </div>
          ) : null}

          <div
            style={{
              ...styles.packageCurrent,
              ...(selectedPackageLimitError
                ? styles.packageCurrentBad
                : selectedPackage
                  ? styles.packageCurrentGood
                  : null),
            }}
          >
            <div>
              <strong>Selected package:</strong>{" "}
              {selectedPackage
                ? `${Number(selectedPackage.page_tier || 0).toLocaleString()} pages`
                : "None"}
            </div>
            <div>
              <strong>Declared contract pages:</strong>{" "}
              {declaredPageCount > 0 ? declaredPageCount.toLocaleString() : "Not available yet"}
            </div>
            {selectedPackageLimitError ? (
              <div style={styles.packageCurrentError}>{selectedPackageLimitError}</div>
            ) : null}
          </div>

          {recommendedLowerPackage ? (
            <div style={styles.packageRecommendation}>
              <div>
                This contract fits your available{" "}
                <strong>{Number(recommendedLowerPackage.page_tier || 0).toLocaleString()}-page package</strong>.
              </div>
              <div style={styles.packageRecommendationActions}>
                <button type="button" style={styles.recommendButton} onClick={onUseRecommendedPackage}>
                  Use smaller package
                </button>
                {higherPackageConfirmationRequired ? (
                  <button type="button" style={styles.confirmButton} onClick={onConfirmSelectedPackage}>
                    Use selected larger package
                  </button>
                ) : (
                  <span style={styles.confirmedText}>Larger package confirmed</span>
                )}
              </div>
            </div>
          ) : null}

        </>
      ) : null}
    </div>
  );
}

function MiniStep({ number, title, text, last = false }) {
  return (
    <div style={styles.miniStep}>
      <div style={styles.miniStepLeft}>
        <div style={styles.miniStepNumber}>{number}</div>
        {!last ? <div style={styles.miniStepLine} /> : null}
      </div>
      <div style={styles.miniStepBody}>
        <div style={styles.miniStepTitle}>{title}</div>
        <div style={styles.miniStepText}>{text}</div>
      </div>
    </div>
  );
}

function GuidanceRow({ icon, title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={styles.guidanceRow}>
      <button
        type="button"
        style={styles.guidanceButton}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <span style={styles.guidanceIcon}>{icon}</span>
        <span style={styles.guidanceTitle}>{title}</span>
        <span style={styles.guidanceChevron}>{open ? "−" : "⌄"}</span>
      </button>
      {open ? <div style={styles.guidanceBody}>{children}</div> : null}
    </div>
  );
}

function UploadModal({ phase, progress, fileName, jobId }) {
  const percent = Math.max(0, Math.min(100, Number(progress?.percent || 0)));
  return (
    <div style={styles.modalBackdrop} role="dialog" aria-modal="true" aria-label="Preparing contract analysis">
      <div style={styles.modalCard}>
        <div style={styles.modalSpinner} />
        <h2 style={styles.modalTitle}>Preparing your contract analysis</h2>
        <p style={styles.modalPhase}>{phase || "Preparing your secure upload…"}</p>
        {fileName ? <p style={styles.modalFileName}>{fileName}</p> : null}
        <div style={styles.modalProgressTrack}>
          <div style={{ ...styles.modalProgressFill, width: `${Math.max(4, percent)}%` }} />
        </div>
        <div style={styles.modalStats}>
          <span>{Math.round(percent)}%</span>
          <span>{formatBytes(progress?.loadedBytes || 0)} / {formatBytes(progress?.totalBytes || 0)}</span>
          <span>{formatUploadSpeed(progress?.averageBytesPerSecond || 0)}</span>
          <span>{formatDuration(progress?.elapsedMs || 0)}</span>
        </div>
        <div style={styles.modalWarning}>
          Please keep this browser open while the upload finishes. Once the status page opens, you can safely leave and return from Analysis History.
        </div>
        {jobId ? <div style={styles.modalJob}>Analysis created. Opening the status page…</div> : null}
      </div>
    </div>
  );
}

const styles = {
  outerWrap: {
    width: "100%",
    maxWidth: 1120,
    minWidth: 0,
    margin: "0 auto",
    padding: "18px 16px 44px",
    background:
      "radial-gradient(ellipse 82% 68% at 58% 28%, rgba(37,99,235,0.08) 0%, rgba(37,99,235,0.04) 46%, rgba(37,99,235,0) 100%), radial-gradient(ellipse 72% 58% at 36% 72%, rgba(59,130,246,0.045) 0%, rgba(59,130,246,0.022) 48%, rgba(59,130,246,0) 100%), #FFFFFF",
    color: "#0F172A",
    fontFamily: "sans-serif",
    minHeight: "100vh",
    overflowX: "hidden",
    boxSizing: "border-box",
  },
  outerWrapMobile: { padding: "14px 12px 34px" },
  pageHeader: { marginBottom: 18, paddingTop: 12 },
  pageHeaderText: { minWidth: 0 },
  eyebrow: { color: "#2563EB", fontSize: 13, fontWeight: 950, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 7 },
  eyebrowMobile: { textAlign: "center" },
  h1: { margin: 0, fontSize: 30, lineHeight: 1.12, fontWeight: 950, letterSpacing: "-0.035em" },
  h1Mobile: { fontSize: 28, lineHeight: 1.18, textAlign: "center" },
  subtitle: { margin: "8px 0 0", color: "#475569", fontSize: 15, lineHeight: 1.55, fontWeight: 650, maxWidth: 840 },
  subtitleMobile: { textAlign: "center", marginLeft: "auto", marginRight: "auto" },
  featureGrid: { display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 14, marginBottom: 16 },
  featureGridMobile: { gridTemplateColumns: "1fr", gap: 10 },
  featureTile: { display: "flex", alignItems: "center", gap: 12, minHeight: 82, padding: 14, borderRadius: 16, border: "1px solid rgba(2,6,23,0.10)", background: "#FFFFFF", boxShadow: "0 10px 26px rgba(2,6,23,0.055)" },
  featureIcon: { width: 44, height: 44, minWidth: 44, borderRadius: 14, background: "rgba(37,99,235,0.10)", color: "#2563EB", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 950 },
  featureTitle: { margin: "0 0 5px", fontSize: 14.5, fontWeight: 950, lineHeight: 1.25 },
  featureText: { color: "#475569", fontSize: 13.5, lineHeight: 1.42, fontWeight: 650 },
  pageGrid: { display: "grid", gridTemplateColumns: "minmax(0,1fr) 290px", gap: 16, alignItems: "start" },
  pageGridMobile: { gridTemplateColumns: "1fr" },
  mainColumn: { minWidth: 0 },
  workflowPanel: {
    display: "grid",
    gap: 14,
    borderRadius: 18,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#F8FAFC",
    boxShadow: "0 14px 34px rgba(2,6,23,0.07)",
    padding: 14,
    overflow: "visible",
  },
  stepSection: {
    minWidth: 0,
    border: "1px solid rgba(2,6,23,0.10)",
    borderRadius: 17,
    background: "#FFFFFF",
    padding: 16,
    boxShadow: "0 7px 20px rgba(2,6,23,0.035)",
  },
  stepHeader: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  stepHeadingGroup: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  stepBadge: {
    width: 38,
    height: 38,
    flex: "0 0 38px",
    borderRadius: 999,
    background: "#2563EB",
    border: "1px solid #2563EB",
    color: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 15,
    fontWeight: 950,
    boxShadow: "0 6px 14px rgba(37,99,235,0.18)",
  },
  stepTitleBlock: {
    minWidth: 0,
  },
  stepTitleRow: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  h2: {
    minWidth: 0,
    margin: 0,
    fontSize: 20,
    lineHeight: 1.25,
    fontWeight: 950,
    letterSpacing: "-0.3px",
  },
  requiredPill: {
    flex: "0 0 auto",
    border: "1px solid #FECACA",
    borderRadius: 999,
    background: "#FEF2F2",
    color: "#B91C1C",
    padding: "4px 7px",
    fontSize: 9.5,
    lineHeight: 1,
    fontWeight: 950,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  stepToggleButton: {
    width: 38,
    height: 38,
    flex: "0 0 38px",
    border: "1px solid rgba(2,6,23,0.08)",
    borderRadius: 13,
    background: "#F1F5F9",
    color: "#0F172A",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    cursor: "pointer",
    fontSize: 21,
    lineHeight: 1,
    fontWeight: 900,
    transition:
      "background 130ms ease, border-color 130ms ease, transform 130ms ease",
  },
  stepBody: {
    width: "100%",
    minWidth: 0,
    paddingTop: 16,
  },
  stepBodyHidden: {
    display: "none",
  },
  schemaWrap: { width: "100%" },
  helperLine: { display: "flex", alignItems: "flex-start", gap: 8, marginTop: 9, color: "#64748B", fontSize: 13.25, lineHeight: 1.45, fontWeight: 700 },
  infoDot: { width: 18, height: 18, minWidth: 18, borderRadius: 999, display: "inline-flex", alignItems: "center", justifyContent: "center", background: "rgba(30,42,120,0.08)", border: "1px solid rgba(30,42,120,0.14)", color: "#1E2A78", fontWeight: 950, fontSize: 11 },
  packageCard: { borderRadius: 16, border: "1px solid rgba(37,99,235,0.18)", background: "#EFF6FF", padding: 12 },
  packageTop: { display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" },
  packageHeaderButton: { appearance: "none", border: "none", background: "transparent", padding: 0, display: "flex", gap: 9, textAlign: "left", cursor: "pointer", flex: 1 },
  packageToggle: { width: 24, height: 24, minWidth: 24, borderRadius: 999, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#1D4ED8", background: "#FFFFFF", fontWeight: 950, border: "1px solid rgba(37,99,235,0.22)" },
  packageTitle: { display: "block", fontWeight: 950, fontSize: 14 },
  packageSub: { display: "block", color: "#1E40AF", fontWeight: 700, fontSize: 12, marginTop: 3, lineHeight: 1.45 },
  packageRefreshButton: { border: "1px solid rgba(37,99,235,0.22)", background: "#FFFFFF", color: "#1D4ED8", borderRadius: 999, padding: "7px 10px", fontWeight: 900, cursor: "pointer", whiteSpace: "nowrap" },
  packageError: { marginTop: 10, color: "#991B1B", background: "#FEF2F2", border: "1px solid rgba(220,38,38,0.18)", borderRadius: 12, padding: 10, fontSize: 12, fontWeight: 800, lineHeight: 1.4 },
  packageEmpty: { marginTop: 10, color: "#334155", background: "#FFFFFF", border: "1px solid rgba(15,23,42,0.08)", borderRadius: 13, padding: 10, fontSize: 12, fontWeight: 750, lineHeight: 1.4 },
  packageBillingButton: { marginTop: 9, width: "100%", border: "none", borderRadius: 12, background: "#0F172A", color: "#FFFFFF", padding: "9px 10px", fontWeight: 950, cursor: "pointer" },
  packageList: { marginTop: 10, display: "grid", gap: 8 },
  packageRow: { display: "grid", gridTemplateColumns: "auto minmax(0,1fr) auto", alignItems: "center", gap: 10, borderRadius: 13, background: "#FFFFFF", border: "1px solid rgba(15,23,42,0.08)", padding: "11px 12px", cursor: "pointer" },
  packageRowSelected: { borderColor: "rgba(37,99,235,0.52)", boxShadow: "0 0 0 3px rgba(37,99,235,0.10)", background: "#F8FBFF" },
  packageRadio: { width: 17, height: 17, accentColor: "#2563EB", cursor: "pointer" },
  packageRowMain: { minWidth: 0, display: "grid", gap: 3 },
  packageRowTitle: { fontSize: 13, fontWeight: 950 },
  packageRowMeta: { color: "#64748B", fontSize: 11, fontWeight: 750, lineHeight: 1.35 },
  packageBreakdown: { color: "#475569", fontSize: 11, fontWeight: 800 },
  packageRemaining: { color: "#1D4ED8", fontSize: 11, fontWeight: 950, textAlign: "right", whiteSpace: "nowrap" },
  packageCurrent: { marginTop: 10, borderRadius: 13, padding: 10, background: "#F8FAFC", border: "1px solid rgba(15,23,42,0.08)", color: "#334155", fontSize: 12, fontWeight: 750, lineHeight: 1.5 },
  packageCurrentGood: { background: "#F0FDF4", borderColor: "rgba(22,163,74,0.18)" },
  packageCurrentBad: { background: "#FEF2F2", borderColor: "rgba(220,38,38,0.18)" },
  packageCurrentError: { marginTop: 5, color: "#991B1B", fontWeight: 850 },
  packageRecommendation: { marginTop: 10, borderRadius: 13, padding: 10, background: "#FFFBEB", border: "1px solid rgba(245,158,11,0.22)", color: "#78350F", fontSize: 12, fontWeight: 750, lineHeight: 1.45 },
  packageRecommendationActions: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 8 },
  recommendButton: { border: "none", borderRadius: 10, padding: "8px 10px", background: "#1D4ED8", color: "#FFFFFF", fontWeight: 900, cursor: "pointer" },
  confirmButton: { border: "1px solid rgba(120,53,15,0.25)", borderRadius: 10, padding: "8px 10px", background: "#FFFFFF", color: "#78350F", fontWeight: 900, cursor: "pointer" },
  confirmedText: { color: "#166534", fontWeight: 900 },
  dropZone: { border: "1.5px dashed rgba(37,99,235,0.55)", borderRadius: 16, background: "linear-gradient(180deg,rgba(37,99,235,0.035),rgba(255,255,255,0.96))", padding: "28px 18px", textAlign: "center" },
  dropIcon: { width: 48, height: 48, margin: "0 auto 10px", borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", color: "#1E2A78", fontSize: 30, fontWeight: 950 },
  dropHeadline: { fontSize: 15.5, fontWeight: 950, marginBottom: 4 },
  dropOr: { color: "#64748B", fontSize: 13, fontWeight: 750, marginBottom: 10 },
  primaryButton: { appearance: "none", border: "1px solid rgba(37,99,235,0.20)", background: "#2563EB", color: "#FFFFFF", borderRadius: 12, padding: "11px 18px", fontSize: 14, fontWeight: 950, cursor: "pointer", boxShadow: "0 12px 24px rgba(37,99,235,0.20)", minWidth: 150 },
  fileHint: { margin: "13px auto 0", maxWidth: 520, textAlign: "center", lineHeight: 1.4 },
  fileHintTitle: { color: "#334155", fontSize: 12.75, fontWeight: 900 },
  fileHintMeta: { marginTop: 3, color: "#64748B", fontSize: 12, fontWeight: 750 },
  selectedFileRow: { marginTop: 12, display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 14, border: "1px solid rgba(2,6,23,0.10)", background: "#FFFFFF", boxShadow: "0 8px 20px rgba(2,6,23,0.04)" },
  fileIcon: { width: 40, height: 40, minWidth: 40, borderRadius: 10, background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.18)", color: "#EF4444", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 950 },
  selectedFileTextWrap: { minWidth: 0, flex: 1 },
  selectedFileName: { fontSize: 13.5, fontWeight: 850, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  selectedFileMeta: { color: "#64748B", fontSize: 12, fontWeight: 750, marginTop: 2 },
  removeButton: { border: "none", background: "transparent", color: "#334155", fontSize: 24, cursor: "pointer", padding: "4px 6px" },
  manualPageField: { display: "grid", gap: 8, marginTop: 12, padding: "14px 12px", borderRadius: 14, border: "1px solid rgba(37,99,235,0.16)", background: "#F8FAFC", textAlign: "center" },
  manualPageLabel: { color: "#0F172A", fontSize: 14, lineHeight: 1.3, fontWeight: 950 },
  manualPageInput: { width: "100%", maxWidth: 260, justifySelf: "center", border: "1px solid rgba(2,6,23,0.16)", borderRadius: 12, padding: "11px 12px", fontSize: 15, textAlign: "center", outline: "none" },
  manualPageHelp: { maxWidth: 560, margin: "0 auto", color: "#64748B", fontSize: 12, lineHeight: 1.45, fontWeight: 700 },
  previewWrap: { marginTop: 12, border: "1px solid rgba(2,6,23,0.10)", borderRadius: 14, padding: 10, background: "#F8FAFC" },
  previewTitle: { fontWeight: 950, marginBottom: 8, fontSize: 13 },
  previewImage: { maxWidth: "100%", maxHeight: 360, objectFit: "contain", borderRadius: 10, display: "block", margin: "0 auto" },
  termsLabel: { display: "inline-flex", alignItems: "flex-start", gap: 9, fontSize: 13.5, color: "#334155", lineHeight: 1.5, marginBottom: 12, fontWeight: 750 },
  termsCheckbox: { marginTop: 3, width: 16, height: 16, accentColor: "#2563EB" },
  inlineError: { marginTop: 8, fontSize: 12.8, color: "#B91C1C", fontWeight: 850, lineHeight: 1.42 },
  notice: { margin: "4px 0 12px", borderRadius: 13, padding: "10px 12px", fontSize: 13, lineHeight: 1.45, fontWeight: 750, border: "1px solid" },
  noticeOk: { background: "rgba(5,150,105,0.08)", color: "#065F46", borderColor: "rgba(5,150,105,0.18)" },
  noticeError: { background: "rgba(185,28,28,0.06)", color: "#991B1B", borderColor: "rgba(185,28,28,0.16)" },
  noticeBilling: { background: "rgba(245,158,11,0.08)", color: "#92400E", borderColor: "rgba(245,158,11,0.20)" },
  noticeSubtext: { marginTop: 5, color: "#475569" },
  analyzeButton: { appearance: "none", border: "1px solid rgba(37,99,235,0.22)", background: "#2563EB", color: "#FFFFFF", borderRadius: 12, padding: "13px 18px", fontSize: 14.5, fontWeight: 950, cursor: "pointer", boxShadow: "0 14px 26px rgba(37,99,235,0.22)", width: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 },
  buttonDisabled: { opacity: 0.58, cursor: "not-allowed", boxShadow: "none" },
  buttonSpinner: { width: 16, height: 16, borderRadius: 999, border: "2px solid rgba(255,255,255,0.45)", borderTopColor: "#FFFFFF", animation: "ci-spin 0.8s linear infinite" },
  sideColumn: { display: "flex", flexDirection: "column", gap: 14, minWidth: 0 },
  sideColumnMobile: { gridRow: 2 },
  sidePanel: { borderRadius: 16, border: "1px solid rgba(2,6,23,0.10)", background: "#FFFFFF", boxShadow: "0 10px 26px rgba(2,6,23,0.055)", padding: 14 },
  sideTitle: { margin: "0 0 12px", fontSize: 14.5, fontWeight: 950, lineHeight: 1.25 },
  sideCopy: { margin: "0 0 10px", color: "#475569", fontSize: 13, lineHeight: 1.55, fontWeight: 700 },
  sideButton: { appearance: "none", border: "1px solid rgba(37,99,235,0.55)", background: "#FFFFFF", color: "#2563EB", borderRadius: 12, padding: "10px 12px", width: "100%", cursor: "pointer", fontWeight: 950, fontSize: 13.5 },
  miniStep: { display: "grid", gridTemplateColumns: "30px minmax(0,1fr)", gap: 9, minHeight: 72 },
  miniStepLeft: { position: "relative", display: "flex", justifyContent: "center" },
  miniStepNumber: { width: 24, height: 24, borderRadius: 999, border: "1px solid rgba(37,99,235,0.45)", background: "#FFFFFF", color: "#2563EB", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 950, zIndex: 1 },
  miniStepLine: { position: "absolute", top: 26, bottom: 2, width: 1, background: "rgba(37,99,235,0.25)" },
  miniStepBody: { paddingBottom: 12 },
  miniStepTitle: { fontSize: 13.25, fontWeight: 950, marginBottom: 4 },
  miniStepText: { color: "#475569", fontSize: 12.5, lineHeight: 1.45, fontWeight: 650 },
  guidanceStack: { marginTop: 14, display: "flex", flexDirection: "column", gap: 8 },
  guidanceRow: { borderRadius: 14, border: "1px solid rgba(2,6,23,0.10)", background: "#FFFFFF", boxShadow: "0 8px 20px rgba(2,6,23,0.04)", overflow: "hidden" },
  guidanceButton: { appearance: "none", width: "100%", border: "none", background: "transparent", cursor: "pointer", padding: "12px 14px", display: "grid", gridTemplateColumns: "30px 1fr 24px", alignItems: "center", gap: 10, textAlign: "left" },
  guidanceIcon: { width: 26, height: 26, borderRadius: 10, background: "rgba(30,42,120,0.07)", color: "#1E2A78", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 950 },
  guidanceTitle: { fontSize: 14, fontWeight: 900 },
  guidanceChevron: { justifySelf: "end", color: "#334155", fontSize: 18, fontWeight: 950 },
  guidanceBody: { padding: "0 14px 14px 54px", color: "#475569", fontSize: 13.25, lineHeight: 1.55, fontWeight: 650 },
  iosNote: { margin: "14px 0 0", color: "#475569", fontSize: 13, lineHeight: 1.5, fontStyle: "italic", fontWeight: 700 },
  modalBackdrop: { position: "fixed", inset: 0, zIndex: 9999, background: "rgba(15,23,42,0.64)", backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
  modalCard: { width: "min(560px,100%)", borderRadius: 20, background: "#FFFFFF", border: "1px solid rgba(255,255,255,0.65)", boxShadow: "0 30px 80px rgba(15,23,42,0.30)", padding: 22, textAlign: "center" },
  modalSpinner: { width: 46, height: 46, borderRadius: 999, margin: "0 auto 12px", border: "4px solid rgba(37,99,235,0.18)", borderTopColor: "#2563EB", animation: "ci-spin 0.9s linear infinite" },
  modalTitle: { margin: 0, fontSize: 22, fontWeight: 950, letterSpacing: "-0.35px" },
  modalPhase: { margin: "8px 0 4px", color: "#334155", fontSize: 14, fontWeight: 800, lineHeight: 1.45 },
  modalFileName: { margin: "0 0 12px", color: "#64748B", fontSize: 12.5, fontWeight: 750, wordBreak: "break-word" },
  modalProgressTrack: { height: 10, background: "rgba(15,23,42,0.09)", borderRadius: 999, overflow: "hidden", marginTop: 12 },
  modalProgressFill: { height: "100%", borderRadius: 999, background: "#2563EB", transition: "width 120ms linear" },
  modalStats: { display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 6, marginTop: 8, color: "#64748B", fontSize: 11.5, fontWeight: 800 },
  modalWarning: { marginTop: 16, borderRadius: 13, padding: 11, background: "#FFFBEB", border: "1px solid rgba(245,158,11,0.24)", color: "#78350F", fontSize: 12.5, lineHeight: 1.5, fontWeight: 850 },
  modalJob: { marginTop: 10, color: "#166534", fontSize: 12, fontWeight: 850 },
};
